// Admin → Codes (spec "Codes"): discount codes for plans, up to 100% off for good, each with a
// sign-up link that fills it in; and founding plans for everyone from before launch.

import { Tag } from '../ui/Tag';
import { IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { admin, codeOff, type Code } from '../api';
import { fullDate } from './Shell';
import { Button } from '../ui/Button';
import { Empty } from '../ui/ds';
import { Select } from '../ui/Select';
import { Dialog } from '../ui/Dialog';

const DURATIONS: { value: Code['duration']; label: string }[] = [
  { value: 'forever', label: 'For good' },
  { value: 'repeating', label: 'For some months' },
  { value: 'once', label: 'First payment' },
];

const SIZES = [
  { value: '', label: 'Any size' },
  { value: 'standard-s', label: 'Standard S' },
  { value: 'standard-m', label: 'Standard M' },
  { value: 'standard-l', label: 'Standard L' },
  { value: 'standard-xl', label: 'Standard XL' },
];

/** FRIEND-7K2Q: easy to read out, no 0/O or 1/I. */
function freshCode(prefix = 'FRIEND') {
  const abc = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const rnd = crypto.getRandomValues(new Uint8Array(4));
  return prefix + '-' + Array.from(rnd, (b) => abc[b % abc.length]).join('');
}

function CopyLink({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="btn"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(`${location.origin}/signup?code=${encodeURIComponent(code)}`);
          setCopied(true);
          setTimeout(() => setCopied(false), 1600);
        } catch {
          /* clipboard refused: the code is on screen */
        }
      }}
      data-testid="copy-code-link"
    >
      {copied ? 'Copied ✓' : 'Copy link'}
    </button>
  );
}

/** Make code: the code's fields in a dialog (opened from the Codes header). */
function NewCode({ onMade, onCancel }: { onMade: (c: Code) => void; onCancel: () => void }) {
  const [code, setCode] = useState(freshCode);
  const [percentOff, setPercentOff] = useState(100);
  const [duration, setDuration] = useState<Code['duration']>('forever');
  const [months, setMonths] = useState(3);
  const [maxUses, setMaxUses] = useState('');
  const [expires, setExpires] = useState('');
  const [tier, setTier] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    const r = await admin.createCode({
      code,
      percentOff,
      duration,
      months: duration === 'repeating' ? months : undefined,
      maxUses: maxUses ? Number(maxUses) : undefined,
      expires: expires ? Math.floor(new Date(expires + 'T23:59:59').getTime() / 1000) : undefined,
      tier: tier || undefined,
    });
    setBusy(false);
    if (!r.ok) return setError(r.error);
    onMade(r.data.code);
  };

  return (
    <form className="form code-form" onSubmit={submit} data-testid="code-form">
      <div className="code-form-grid">
        <div className="field">
          <label htmlFor="code-code">Code</label>
          <div className="input-affix">
            <input id="code-code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} maxLength={40} data-testid="code-code" />
            <button type="button" className="input-affix-btn" onClick={() => setCode(freshCode())} aria-label="Another code" title="Another code">
              <IconRefresh size={16} stroke={2} aria-hidden="true" />
            </button>
          </div>
        </div>
        <div className="field">
          <label htmlFor="code-pct">Off</label>
          <div className="input-affix">
            <input id="code-pct" type="number" inputMode="numeric" min={1} max={100} value={percentOff} onChange={(e) => setPercentOff(Number(e.target.value))} data-testid="code-pct" />
            <span className="input-affix-text" aria-hidden="true">%</span>
          </div>
        </div>
        <div className="field">
          <label htmlFor="code-dur">For</label>
          <Select id="code-dur" value={duration} onChange={(v) => setDuration(v as Code['duration'])} testid="code-duration" options={DURATIONS} />
        </div>
        {duration === 'repeating' && (
          <div className="field">
            <label htmlFor="code-months">Months</label>
            <input id="code-months" type="number" min={1} max={36} value={months} onChange={(e) => setMonths(Number(e.target.value))} />
          </div>
        )}
        <div className="field">
          <label htmlFor="code-size">Size</label>
          <Select id="code-size" value={tier} onChange={setTier} testid="code-size" options={SIZES} />
        </div>
        <div className="field">
          <label htmlFor="code-uses">Uses (optional)</label>
          <input id="code-uses" type="number" min={1} placeholder="unlimited" value={maxUses} onChange={(e) => setMaxUses(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="code-expires">Ends (optional)</label>
          <input id="code-expires" type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
        </div>
      </div>
      <p className="muted small">{codeOff({ percentOff, duration, months })}{tier ? `, ${SIZES.find((s) => s.value === tier)?.label} only` : ''}. A 100% code needs no card.</p>
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      <div className="form-actions">
        <Button onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" type="submit" disabled={busy || !code.trim()} data-testid="create-code">
          {busy ? 'Making…' : 'Make code'}
        </Button>
      </div>
    </form>
  );
}

/** Founding plans as an empty state: what it does, and the one action (safe to run again). */
function Founding() {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  return (
    <Empty
      size="md"
      art="ticket"
      className="table-empty founding"
      data-testid="founding"
      title="Founding members"
      lead="Everyone with a cloud machine from before launch gets a plan at their size, free for good. Safe to run again."
    >
      <Button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          const r = await admin.founding();
          setBusy(false);
          if (!r.ok) return setResult(r.error);
          const failed = Object.entries(r.data.failed);
          setResult(
            `${r.data.done.length} on founding plans.` + (failed.length ? ` Failed: ${failed.map(([e, why]) => `${e} (${why})`).join(', ')}` : ''),
          );
        }}
        data-testid="founding-run"
      >
        {busy ? 'Working…' : 'Give founding plans'}
      </Button>
      {result && <p className="small founding-result" role="status">{result}</p>}
    </Empty>
  );
}

export function AdminCodes() {
  const [codes, setCodes] = useState<Code[] | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [made, setMade] = useState<Code | null>(null);
  const [making, setMaking] = useState(false);
  const load = useCallback(async () => {
    const r = await admin.codes();
    if (r.ok) (setCodes(r.data.codes), setEnabled(r.data.enabled));
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="card" id="codes" aria-labelledby="codes-title" data-testid="admin-codes">
      <div className="card-head">
        <div className="card-title">
          <h2 id="codes-title">Codes</h2>
          <span className="count">{codes?.filter((c) => c.active).length ?? ''}</span>
        </div>
        {enabled && (
          <Button variant="primary" onClick={() => setMaking(true)} data-testid="new-code">
            Make code
          </Button>
        )}
      </div>
      {!enabled ? (
        <p className="muted">Plans aren’t set up on this hub (no Stripe key), so there are no codes.</p>
      ) : (
        <>
          {making && (
            <Dialog title="Make code" wide onClose={() => setMaking(false)} testId="code-dialog">
              <NewCode onMade={(c) => (setMade(c), setMaking(false), void load())} onCancel={() => setMaking(false)} />
            </Dialog>
          )}
          {made && (
            <div className="banner banner-ok" role="status" data-testid="code-made">
              <div>
                <strong>{made.code}</strong> · {codeOff(made)}
                <div className="small muted">
                  <code>{`${location.origin}/signup?code=${made.code}`}</code>
                </div>
              </div>
              <CopyLink code={made.code} />
            </div>
          )}
          <div className="table-wrap table-cards">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">Code</th>
                  <th scope="col">Off</th>
                  <th scope="col">Used</th>
                  <th scope="col">Ends</th>
                  <th scope="col">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {(codes ?? []).map((c) => (
                  <tr key={c.id} data-testid="code-row" data-active={c.active}>
                    <td className="cell-head">
                      <span className="code-cell">
                        <span className="cell-strong mono">{c.code}</span>
                        {!c.active && <Tag tone="red">Off</Tag>}
                      </span>
                    </td>
                    <td data-label="Off">
                      {codeOff(c)}
                      {c.tier ? <span className="muted small"> · {SIZES.find((s) => s.value === c.tier)?.label}</span> : null}
                    </td>
                    <td data-label="Used">
                      {c.used}
                      {c.maxUses ? <span className="muted"> / {c.maxUses}</span> : null}
                    </td>
                    <td className="muted" data-label="Ends">
                      {c.expires ? fullDate(new Date(c.expires * 1000).toISOString()) : '—'}
                    </td>
                    <td className="cell-actions">
                      {c.active && (
                        <div className="row-btns">
                          <CopyLink code={c.code} />
                          <button
                            className="btn btn-ghost btn-ghost-danger"
                            onClick={async () => {
                              const r = await admin.deactivateCode(c.id);
                              if (r.ok) void load();
                            }}
                            data-testid="code-off"
                          >
                            Turn off
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {codes && !codes.length && <Empty size="md" art="ticket" className="table-empty" title="No codes yet" lead="A code you make shows up here." />}
            {!codes && <div className="skeleton" aria-busy="true" aria-label="Loading codes" />}
          </div>
          <Founding />
        </>
      )}
    </section>
  );
}
